import ReactTooltip from 'react-tooltip';
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Button from '../../shared/button.component';
import AgGrid from '../../shared/agGrid/agGrid.component';
import {
  showAdjustmentModalForAddEdit,
} from './store/actions';
import OptionsCellRenderer from './optionsCellRenderer.component';
import AttachmentsCellRenderer from './attachmentsCellRenderer.component';
import {
  alignLeftCellStyle,
} from '@common/cellStyles';
import withPillNavigation from '../../shared/withPillNavigation.decorator';
import { pillsDefinition, pillsTypes } from './pillsDefinition';
import { adjustmentsTabBookToTaxDifferencesColumnDefs } from '../../shared/columnDefinitions/bookToTaxDifferences';
import {
  withAssignments,
  assignmentPermissions,
  assignmentTypes,
} from '../../shared/authorization';
import {
  isCompanyCodeSubledgerReadySelector,
  fetchingPriovisionDataSelector,
} from '../../shared/store/selectors';
import { adjustmentsColumnCollectionDefs } from '../../shared/columnDefinitions/adjustments';
import { AdjustmentsCollectionPropTypes } from '../../shared/propTypes/adjustments';
import { BookToTaxDifferencesPropTypes } from '../../shared/propTypes/bookToTaxDifferences';
import {
  hasAccessSelector,
  adjustmentsSelector,
} from '../store/selectors';
import { useIsClientLocked } from '../../shared/clientLocked/useIsClientLocked.hook';
import { debouncedRebuildReactTooltip } from '../../shared/debouncedRebuildReactTooltip';

const adminColumn = {
  headerName: '',
  cellRenderer: OptionsCellRenderer,
  valueGetter: ({ data }) => data,
  width: 36,
  minWidth: 60,
  ...alignLeftCellStyle,
};

const attachmentsColumn = {
  headerName: '',
  field: 'attachments',
  cellRenderer: AttachmentsCellRenderer,
  width: 32,
  minWidth: 32,
  ...alignLeftCellStyle,
};

adjustmentsColumnCollectionDefs.forEach(column => column.cellRendererParams = { field: column.field });


const AdjustmentsTab = ({
  adjustments: {
    adjustmentsCollection: adjustments,
    bookToTaxDifferencesCollection: bookToTaxDifferences,
  },
  showAdjustmentModalForAddEdit,
  activePill,
  hasAccess,
  fetchingProvisionData,
}) => {
  const isClientLocked = useIsClientLocked();

  useEffect(() => {
    debouncedRebuildReactTooltip();
  }, [isClientLocked]);

  const adminColumnDefinition = hasAccess && !isClientLocked ? [adminColumn] : [];
  const attachmentsColumnDefinition = [attachmentsColumn];
  const columnDefinition = [
    ...adminColumnDefinition,
    ...attachmentsColumnDefinition,
    ...adjustmentsColumnCollectionDefs,
  ];

  return (
    <div className="adjustments-tab">
      <ReactTooltip place="right" effect="float" />
      <div className="adjustments-tab-header">
        {activePill === pillsTypes.ADJUSTMENTS && hasAccess ? (
          <div className="row">
            <div
              className="col-2 ml-auto"
              data-tip={isClientLocked ? 'You are not allowed to add adjustment while client is locked' : ''}
            >
              <Button
                type="button"
                kind="primary"
                className="add-adjustment-button"
                disabled={isClientLocked}
                onClick={() => showAdjustmentModalForAddEdit()} // eslint-disable-line react/jsx-no-bind
              >
                Add Adjustment
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <div
        className="adjustments-grid"
      >
        {activePill === pillsTypes.ADJUSTMENTS && <AgGrid
          withSearchBar
          columnDefs={columnDefinition}
          rowData={adjustments}
          isGridLoading={fetchingProvisionData}
          showCount
        />}

        {activePill === pillsTypes.BOOK_TO_TAX_DIFFERENCES && <AgGrid
          withSearchBar
          columnDefs={adjustmentsTabBookToTaxDifferencesColumnDefs}
          rowData={bookToTaxDifferences}
        />}
      </div>
    </div>
  );
};

AdjustmentsTab.propTypes = {
  adjustments: PropTypes.shape({
    adjustmentsCollection: AdjustmentsCollectionPropTypes,
    bookToTaxDifferencesCollection: BookToTaxDifferencesPropTypes,
  }),
  showAdjustmentModalForAddEdit: PropTypes.func.isRequired,
  activePill: PropTypes.string.isRequired,
  hasAccess: PropTypes.bool.isRequired,
  fetchingProvisionData: PropTypes.bool.isRequired,
};


const mapStateToProps = (state, ownProps) => ({
  adjustments: adjustmentsSelector(state),
  isCompanyCodeSubledgerReady: isCompanyCodeSubledgerReadySelector(state),
  hasAccess: hasAccessSelector(state, ownProps.hasAccessBasedOnAssignments),
  fetchingProvisionData: fetchingPriovisionDataSelector(state),
});

export default withAssignments([
  { assignmentType: assignmentTypes.CC, requiredPermission: assignmentPermissions.Adjust },
])(connect(
  mapStateToProps,
  {
    showAdjustmentModalForAddEdit,
  }
)(withPillNavigation(() => pillsDefinition)(AdjustmentsTab)
));
